import { faMapMarkedAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import { epochToDate, epochToGMTFormatted } from "utility/dateFormat";

interface ViewDetailsProps {
    selectedVisit: any;
    handleDownload: () => void;
    handleDeleteIcon: () => void;
    isDeletedMedia: (value: any) => boolean;
}

const ViewVisitDetails = ({
    selectedVisit,
    handleDownload,
    handleDeleteIcon,
    isDeletedMedia,
}: ViewDetailsProps) => {
    return (
        <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">

                {/* LEFT CARD */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-5 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
                        Visit Details
                    </h3>

                    <div className="space-y-4 text-sm">

                        {/* Distributor */}
                        <div className="flex justify-between">
                            <span className="text-gray-500">Distributor</span>
                            <span className="font-medium text-gray-800 dark:text-gray-200">
                                {selectedVisit.distributorName || "N/A"}
                            </span>
                        </div>

                        {/* Visit Date */}
                        <div className="flex justify-between">
                            <span className="text-gray-500">Visit Date</span>
                            <span className="text-gray-800 dark:text-gray-200">
                                {epochToDate(selectedVisit.createdDate)}
                            </span>
                        </div>

                        {/* ✅ Show Follow-up only when NOT pending */}
                        {!selectedVisit.isPending && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Follow-up</span>
                                <span className="text-gray-800 dark:text-gray-200">
                                    {epochToGMTFormatted(selectedVisit.nextFollowUpVisit)}
                                </span>
                            </div>
                        )}

                        {/* Coordinates */}
                        <div className="flex justify-between">
                            <span className="text-gray-500">Coordinates</span>
                            <span className="text-gray-800 dark:text-gray-200 text-right">
                                {selectedVisit.latitude?.toFixed(6) || "N/A"},{" "}
                                {selectedVisit.longitude?.toFixed(6) || "N/A"}
                            </span>
                        </div>

                        {/* Address */}
                        <div>
                            <span className="text-gray-500">Location</span>
                            <p className="text-gray-800 dark:text-gray-200 mt-1 leading-relaxed">
                                {selectedVisit.visitLocation || "N/A"}
                            </p>
                        </div>

                        {/* Notes */}
                        <div>
                            <span className="text-gray-500">Notes</span>
                            <p className="mt-1 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300">
                                {selectedVisit.note || "No notes provided"}
                            </p>
                        </div>

                        {/* Google Maps */}
                        <div className="pt-3">
                            <a
                                href={`https://maps.google.com/?q=${selectedVisit.latitude},${selectedVisit.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg 
                bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition"
                            >
                                <FontAwesomeIcon icon={faMapMarkedAlt} />
                                Open in Google Maps
                            </a>
                        </div>
                    </div>
                </div>

                {/* RIGHT CARD - IMAGE */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-5 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
                        Visit Image
                    </h3>

                    {selectedVisit.imageUrl ? (
                        <div className="relative group rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">

                            {/* Image */}
                            <img
                                src={selectedVisit.imageUrl}
                                alt="Visit"
                                className="w-full h-[320px] object-contain transition-transform duration-300 group-hover:scale-105"
                            />

                            {/* Overlay Actions */}
                            {!isDeletedMedia(selectedVisit.imageUrl) && (
                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-4">

                                    {/* ✅ FIXED ICON USAGE */}
                                    <button
                                        onClick={handleDownload}
                                        className="bg-white p-3 rounded-full shadow hover:scale-110 transition"
                                    >
                                        {FiDownload({ className: "w-5 h-5 text-gray-700" }) as JSX.Element}
                                    </button>

                                    <button
                                        onClick={handleDeleteIcon}
                                        className="bg-white p-3 rounded-full shadow hover:bg-red-100 hover:scale-110 transition"
                                    >
                                        {FiTrash2({ className: "w-5 h-5 text-red-600" }) as JSX.Element}
                                    </button>

                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-[320px] bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-400">
                            <span className="text-lg">📷</span>
                            <p className="mt-2 text-sm">No image available</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewVisitDetails;